import axios, { isAxiosError } from 'axios';

import { apiClient, getCsrfToken, getApiBaseUrlEnv } from '@/lib/api-client';

import {
  acknowledgeSignature,
  listAllQueuedSignatures,
  updateQueuedSignature,
} from '@/lib/offline/outbox';
import { getOfflineSessionOwner, isOwnedBy } from '@/lib/offline/session-offline-data';
import {
  clearKioskCredential,
  getKioskCredential,
} from '@/lib/offline/kiosk-credential-vault';
import { getCachedSigningToken } from '@/lib/offline/shift-signing-token-cache';

/** Kiosk-sign endpoints authenticate the signer, not the supervisor session. */
function isKioskEndpoint(endpoint: string): boolean {
  return endpoint.includes('/kiosk-sign/');
}

function apiBaseUrl(): string {
  return (
    getApiBaseUrlEnv()
    || (import.meta.env.PROD ? 'https://api.h3ight.com' : 'http://localhost:8000')
  ).replace(/\/+$/, '');
}

/**
 * POST a kiosk signature using a bare axios call (not apiClient) so that an
 * invalid-credential 401 does NOT trigger the supervisor's token-refresh
 * interceptor and bounce them to /login — mirroring the KioskSignModal.
 */
async function postKioskSignature(endpoint: string, body: Record<string, unknown>): Promise<void> {
  const headers: Record<string, string> = {};
  const csrf = getCsrfToken();
  if (csrf) {
    headers['X-CSRFToken'] = csrf;
  }
  const url = `${apiBaseUrl()}${endpoint}`;
  await axios.post(url, body, { withCredentials: true, headers });
}

const KIOSK_TOKEN_MISSING_MESSAGE =
  'Signing token is no longer available (the session ended before this signature synced). Please sign again.';
const KIOSK_CREDENTIAL_MISSING_MESSAGE =
  'Signing credentials are no longer available (the session ended before this signature synced). Please sign again.';

export interface SyncResult {
  synced: number;
  conflicts: number;
  failed: number;
  held: number;
  jsaIds: number[];
}

export const MAX_SYNC_ATTEMPTS = 5;

let syncing = false;

function extractMessage(error: unknown): string {
  if (isAxiosError<{ error?: string }>(error) && error.response?.data?.error) {
    return error.response.data.error;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Failed to sync signature';
}

const SIGNATURE_REVOKED_MESSAGE = 'Signature was revoked — sign again.';

function friendlyConflictMessage(data: { error?: string; code?: string; jsa_status?: string } | undefined): string {
  if (data?.code === 'signature_revoked') {
    return SIGNATURE_REVOKED_MESSAGE;
  }
  if (data?.error) {
    return data.error;
  }
  return 'This signature could not be recorded because the JSA is no longer accepting signatures.';
}

export async function syncOutbox(): Promise<SyncResult> {
  if (syncing) {
    return { synced: 0, conflicts: 0, failed: 0, held: 0, jsaIds: [] };
  }

  syncing = true;
  try {
    const allRecords = await listAllQueuedSignatures();
    const owner = getOfflineSessionOwner();
    const records = allRecords.filter((record) => isOwnedBy(record, owner));
    const result: SyncResult = {
      synced: 0,
      conflicts: 0,
      failed: 0,
      held: allRecords.length - records.length,
      jsaIds: [],
    };
    const syncedJsaIds = new Set<number>();

    for (const item of records) {
      if (!isOwnedBy(item, getOfflineSessionOwner())) continue;
      if (item.status === 'conflict') {
        continue;
      }
      if (item.status === 'failed' && item.attempts >= MAX_SYNC_ATTEMPTS) {
        continue;
      }

      const kiosk = isKioskEndpoint(item.endpoint);
      const username = typeof item.body.username === 'string' ? item.body.username : '';

      // Kiosk replay needs the cached shift signing token and the transient
      // credential (PIN) held only in memory. If either is gone, the server
      // cannot validate the signature, so surface an actionable re-auth prompt.
      let token: string | null = null;
      let credential: Record<string, unknown> = {};
      if (kiosk) {
        const cached = username ? await getCachedSigningToken(username) : null;
        if (!cached) {
          await updateQueuedSignature(item.id, {
            status: 'failed',
            attempts: item.attempts + 1,
            error: KIOSK_TOKEN_MISSING_MESSAGE,
          });
          result.failed += 1;
          continue;
        }
        token = cached.token;

        const stored = getKioskCredential(item.idempotencyKey);
        if (!stored) {
          await updateQueuedSignature(item.id, {
            status: 'failed',
            attempts: item.attempts + 1,
            error: KIOSK_CREDENTIAL_MISSING_MESSAGE,
          });
          result.failed += 1;
          continue;
        }
        credential = { ...stored };
      }

      const payload = {
        ...item.body,
        ...credential,
        ...(token ? { token } : {}),
        client_captured_at: new Date(item.capturedAt).toISOString(),
        client_idempotency_key: item.idempotencyKey,
        offline: true,
      };

      if (!isOwnedBy(item, getOfflineSessionOwner())) continue;
      try {
        if (kiosk) {
          await postKioskSignature(item.endpoint, payload);
        } else {
          await apiClient.post(item.endpoint, payload);
        }
        if (kiosk) {
          clearKioskCredential(item.idempotencyKey);
        }
        await acknowledgeSignature(item.id);
        if (item.jsaId) {
          syncedJsaIds.add(item.jsaId);
        }
        result.synced += 1;
      } catch (error: unknown) {
        if (isAxiosError(error) && error.response?.status === 409 && error.response.data?.code) {
          if (kiosk) {
            clearKioskCredential(item.idempotencyKey);
          }
          await updateQueuedSignature(item.id, {
            status: 'conflict',
            error: friendlyConflictMessage(error.response.data),
          });
          result.conflicts += 1;
          continue;
        }

        // A 401 means the credential (PIN) is wrong or the token is no longer
        // valid. Drop the in-memory credential so the user must re-sign; the
        // cached token is retained (a new PIN can be retried with the same token).
        if (kiosk && isAxiosError(error) && error.response?.status === 401) {
          clearKioskCredential(item.idempotencyKey);
          await updateQueuedSignature(item.id, {
            status: 'failed',
            attempts: item.attempts + 1,
            error: KIOSK_CREDENTIAL_MISSING_MESSAGE,
          });
          result.failed += 1;
          continue;
        }

        await updateQueuedSignature(item.id, {
          status: 'failed',
          attempts: item.attempts + 1,
          error: extractMessage(error),
        });
        result.failed += 1;
      }
    }

    result.jsaIds = Array.from(syncedJsaIds);
    return result;
  } finally {
    syncing = false;
  }
}
